import type { SupabaseClient, User, UserMetadata } from '@supabase/supabase-js';
import { supabaseConfiguration } from '../config/supabase';
import { imageService } from './imageService';

export const DISPLAY_NAME_MAX_LENGTH = 80;
export const AVATAR_BUCKET = 'leitour-avatars';
export const AVATAR_MAX_BYTES = 400_000;

export interface AvatarReference {
  path: string;
  version: string;
}

export const getDisplayName = (user: User | null | undefined): string => {
  const value = user?.user_metadata?.display_name;
  return typeof value === 'string' && value.trim() ? value.trim() : 'Leitor';
};

export const getFirstName = (user: User | null | undefined): string =>
  getDisplayName(user).split(/\s+/u)[0] ?? 'Leitor';

export const getAvatarReference = (user: User | null | undefined): AvatarReference | null => {
  if (!user) return null;
  const path = user.user_metadata?.avatar_path;
  const version = user.user_metadata?.avatar_version;
  if (
    typeof path !== 'string'
    || typeof version !== 'string'
    || !version.trim()
    || version !== version.trim()
    || version.includes('/')
  ) return null;
  const webpPath = `${user.id}/${version}/avatar.webp`;
  const jpegPath = `${user.id}/${version}/avatar.jpg`;
  return path === webpPath || path === jpegPath ? { path, version } : null;
};

export const validateDisplayName = (value: string): string => {
  const normalized = value.trim();
  if (!normalized) throw new Error('Informe um nome para o perfil.');
  if (normalized.length > DISPLAY_NAME_MAX_LENGTH) {
    throw new Error(`O nome deve ter no máximo ${DISPLAY_NAME_MAX_LENGTH} caracteres.`);
  }
  return normalized;
};

export interface AvatarTransport {
  download(path: string): Promise<Blob>;
  remove(path: string): Promise<void>;
  upload(path: string, blob: Blob): Promise<void>;
}

export interface AvatarCache {
  clear(userId: string): Promise<void>;
  get(userId: string, reference: AvatarReference): Promise<Blob | null>;
  put(userId: string, reference: AvatarReference, blob: Blob): Promise<void>;
}

const CACHE_NAME = 'leitour-avatar-v1';
const CACHE_PREFIX = '/__leitour-avatar-cache__/';

const cacheUrl = (userId: string, reference: AvatarReference) => {
  const origin = typeof window === 'undefined' ? 'https://leitour.invalid' : window.location.origin;
  return `${origin}${CACHE_PREFIX}${encodeURIComponent(userId)}/${encodeURIComponent(reference.version)}/${encodeURIComponent(reference.path)}`;
};

export const createAvatarCache = (storage: CacheStorage | undefined = globalThis.caches): AvatarCache => ({
  async get(userId, reference) {
    if (!storage) return null;
    const response = await (await storage.open(CACHE_NAME)).match(cacheUrl(userId, reference));
    return response?.blob() ?? null;
  },
  async put(userId, reference, blob) {
    if (!storage) return;
    const cache = await storage.open(CACHE_NAME);
    await cache.put(cacheUrl(userId, reference), new Response(blob, {
      headers: { 'Content-Type': blob.type || 'application/octet-stream' }
    }));
    const prefix = `${CACHE_PREFIX}${encodeURIComponent(userId)}/`;
    const currentUrl = cacheUrl(userId, reference);
    await Promise.all((await cache.keys())
      .filter((request) => new URL(request.url).pathname.startsWith(prefix) && request.url !== currentUrl)
      .map((request) => cache.delete(request)));
  },
  async clear(userId) {
    if (!storage) return;
    const cache = await storage.open(CACHE_NAME);
    const prefix = `${CACHE_PREFIX}${encodeURIComponent(userId)}/`;
    await Promise.all((await cache.keys())
      .filter((request) => new URL(request.url).pathname.startsWith(prefix))
      .map((request) => cache.delete(request)));
  }
});

type AvatarStorageClient = Pick<SupabaseClient, 'storage'>;

export const createSupabaseAvatarTransport = (client: AvatarStorageClient | null): AvatarTransport => {
  const requireClient = () => {
    if (!client) throw new Error('O serviço de fotos não está configurado neste ambiente.');
    return client;
  };
  return {
    async upload(path, blob) {
      const { error } = await requireClient().storage.from(AVATAR_BUCKET).upload(path, blob, {
        contentType: blob.type,
        upsert: false
      });
      if (error) throw new Error('Não foi possível enviar a nova foto.', { cause: error });
    },
    async download(path) {
      const { data, error } = await requireClient().storage.from(AVATAR_BUCKET).download(path);
      if (error || !data) throw new Error('Não foi possível baixar a foto do perfil.');
      return data;
    },
    async remove(path) {
      const { error } = await requireClient().storage.from(AVATAR_BUCKET).remove([path]);
      if (error) throw new Error('Não foi possível remover a foto antiga.');
    }
  };
};

type MetadataUpdater = (patch: UserMetadata) => Promise<User>;
type CurrentUserReader = () => Promise<User>;
type AvatarProcessor = (file: File) => Promise<Blob>;

const userReferencesAvatar = (user: User, reference: AvatarReference) => {
  const current = getAvatarReference(user);
  return current?.path === reference.path && current.version === reference.version;
};

export class ProfileService {
  constructor(
    private readonly transport: AvatarTransport,
    private readonly cache: AvatarCache,
    private readonly processAvatar: AvatarProcessor = imageService.processAvatar
  ) {}

  async getCachedAvatar(user: User): Promise<Blob | null> {
    const reference = getAvatarReference(user);
    return reference ? this.cache.get(user.id, reference) : null;
  }

  async refreshAvatar(user: User): Promise<Blob | null> {
    const reference = getAvatarReference(user);
    if (!reference) return null;
    const blob = await this.transport.download(reference.path);
    if (!blob.size || blob.size > AVATAR_MAX_BYTES || !['image/webp', 'image/jpeg'].includes(blob.type)) {
      throw new Error('A foto remota do perfil é inválida.');
    }
    await this.cache.put(user.id, reference, blob);
    return blob;
  }

  async replaceAvatar(
    user: User,
    file: File,
    updateMetadata: MetadataUpdater,
    readCurrentUser: CurrentUserReader
  ): Promise<{ blob: Blob; user: User }> {
    const previous = getAvatarReference(user);
    const blob = await this.processAvatar(file);
    const version = crypto.randomUUID();
    const extension = blob.type === 'image/webp' ? 'webp' : 'jpg';
    const reference = { path: `${user.id}/${version}/avatar.${extension}`, version };
    await this.transport.upload(reference.path, blob);

    let updatedUser: User;
    try {
      updatedUser = await updateMetadata({ avatar_path: reference.path, avatar_version: version });
    } catch (updateError) {
      let currentUser: User;
      try {
        currentUser = await readCurrentUser();
      } catch {
        // A confirmação é ambígua: preservar o objeto é mais seguro que quebrar uma referência válida.
        throw updateError;
      }
      if (userReferencesAvatar(currentUser, reference)) {
        await this.cache.put(user.id, reference, blob).catch(() => undefined);
        return { blob, user: currentUser };
      }
      await this.transport.remove(reference.path).catch(() => undefined);
      throw updateError;
    }

    await this.cache.put(user.id, reference, blob).catch(() => undefined);
    if (previous && previous.path !== reference.path) {
      await this.transport.remove(previous.path).catch(() => undefined);
    }
    return { blob, user: updatedUser };
  }

  async removeAvatar(user: User, updateMetadata: MetadataUpdater): Promise<User> {
    const previous = getAvatarReference(user);
    const updatedUser = await updateMetadata({ avatar_path: null, avatar_version: null });
    await this.cache.clear(user.id).catch(() => undefined);
    if (previous) await this.transport.remove(previous.path).catch(() => undefined);
    return updatedUser;
  }
}

export const profileService = new ProfileService(
  createSupabaseAvatarTransport(supabaseConfiguration.client),
  createAvatarCache()
);
